/**
 * ERRORS-1 — the recording-problem catalogue (docs/specs/errors-1.md).
 *
 * One stable, machine-readable code per way a recording can fail, end to end, each with the
 * plain-language copy a person needs to fix it themselves. Lives in `@scribe/core` so the web
 * app and any future TS client render the SAME words for the SAME failure; native clients
 * (Swift/Kotlin) mirror this table — the codes are the contract, the copy may be localized.
 *
 * Codes are append-only. Never rename or repurpose one: the backend persists
 * `Recording.failure_code` and bug reports carry these codes, so a rename would orphan history.
 *
 * Copy rules (spec §3): say what happened, never blame the user, never claim audio was lost
 * unless it provably was, and number the steps so a support thread can say "step 2".
 */
const REPORT_STEP = "If it keeps happening, use “Report this problem” so we can look into it.";
export const RECORDING_PROBLEMS = {
    mic_permission_denied: {
        title: "Microphone access is blocked.",
        cause: "Your browser didn’t allow Scribe to use the microphone, so nothing was recorded.",
        steps: [
            "Click the lock or site-settings icon next to the address bar.",
            "Set Microphone to Allow for this site.",
            "Reload the page, then start the recording again.",
        ],
        action: "grant_microphone",
        actionLabel: "Grant microphone access",
        audio: "none_captured",
        area: "recording",
        reportable: false,
        server: false,
    },
    mic_not_found: {
        title: "No microphone was found.",
        cause: "Scribe couldn’t find a microphone on this device, so nothing was recorded.",
        steps: [
            "Plug in or connect a microphone or headset.",
            "Check it’s selected as the input device in your system sound settings.",
            "Start the recording again.",
        ],
        action: "restart_recording",
        actionLabel: "Try again",
        audio: "none_captured",
        area: "recording",
        reportable: false,
        server: false,
    },
    mic_in_use: {
        title: "The microphone is busy.",
        cause: "Another app or tab is holding the microphone (or the system blocked it), so Scribe couldn’t start recording.",
        steps: [
            "Close other apps or tabs that might be using the microphone (calls, dictation, other recorders).",
            "Start the recording again.",
            "If it still fails, restart the browser.",
        ],
        action: "restart_recording",
        actionLabel: "Try again",
        audio: "none_captured",
        area: "recording",
        reportable: false,
        server: false,
    },
    capture_unsupported: {
        title: "This browser can’t record here.",
        cause: "Recording needs a secure (https) page and a current browser with audio-worklet support, and this one doesn’t qualify.",
        steps: [
            "Open Scribe in a current version of Chrome, Edge, Firefox or Safari.",
            "Make sure the address starts with https://.",
            REPORT_STEP,
        ],
        action: "report",
        actionLabel: "Report this problem",
        audio: "none_captured",
        area: "recording",
        reportable: true,
        server: false,
    },
    capture_device_lost: {
        title: "The microphone disconnected.",
        cause: "The microphone stopped sending audio in the middle of the recording — it was probably unplugged or switched off.",
        steps: [
            "Press “Stop and save” to keep everything recorded so far.",
            "Reconnect the microphone, then start a new recording to capture the rest.",
        ],
        action: "stop_recording",
        actionLabel: "Stop and save",
        audio: "partial",
        area: "recording",
        reportable: false,
        server: false,
    },
    tab_audio_not_shared: {
        title: "Tab audio wasn’t shared.",
        cause: "The screen-share prompt was accepted without “Share tab audio”, so there was no meeting audio to record.",
        steps: [
            "Start the recording again.",
            "In the share prompt, pick the meeting tab and turn on “Share tab audio”.",
        ],
        action: "restart_recording",
        actionLabel: "Try again",
        audio: "none_captured",
        area: "recording",
        reportable: false,
        server: false,
    },
    capture_service_unavailable: {
        title: "Recording isn’t available in this build.",
        cause: "Recording is unavailable in this build — no capture service is configured. Nothing was captured.",
        steps: ["Use the deployed Scribe site, or ask whoever runs this environment to configure the capture service."],
        action: "none",
        audio: "none_captured",
        area: "web",
        reportable: false,
        server: false,
    },
    offline: {
        title: "You’re offline.",
        cause: "This device lost its internet connection.",
        steps: ["Reconnect to Wi-Fi or your network.", "Try again once you’re back online."],
        action: "check_connection",
        actionLabel: "Try again",
        audio: "unknown",
        area: "web",
        reportable: false,
        server: false,
    },
    connection_lost: {
        title: "Connection lost.",
        cause: "Scribe lost its connection to the server and couldn’t reconnect.",
        steps: [
            "Check your network connection.",
            "Start a new session to keep going.",
            REPORT_STEP,
        ],
        action: "restart_recording",
        actionLabel: "Restart",
        audio: "unknown",
        area: "recording",
        reportable: false,
        server: false,
    },
    server_protocol_error: {
        title: "Unexpected response from the server.",
        cause: "The server replied in a way this version of the app didn’t understand.",
        steps: ["Reload the page to pick up the latest version of Scribe.", REPORT_STEP],
        action: "reload",
        actionLabel: "Reload",
        audio: "unknown",
        area: "web",
        reportable: true,
        server: false,
    },
    upload_failed: {
        title: "The upload didn’t finish.",
        cause: "Part of the audio file couldn’t be sent after several tries. The file is still on this device.",
        steps: [
            "Keep this tab open and check your network connection.",
            "Click “Retry upload”.",
            REPORT_STEP,
        ],
        action: "retry_upload",
        actionLabel: "Retry upload",
        audio: "unknown",
        area: "upload",
        reportable: false,
        server: false,
    },
    upload_link_expired: {
        title: "The upload link expired.",
        cause: "The secure upload link timed out before the file finished sending.",
        steps: ["Import the file again — a fresh upload link is created each time."],
        action: "retry_upload",
        actionLabel: "Import again",
        audio: "unknown",
        area: "upload",
        reportable: false,
        server: false,
    },
    session_expired: {
        title: "Your session expired — sign in again.",
        cause: "Your Microsoft sign-in timed out, so Scribe couldn’t confirm who you are.",
        steps: ["Click “Sign in again”.", "Once you’re back, retry what you were doing."],
        action: "sign_in",
        actionLabel: "Sign in again",
        audio: "unknown",
        area: "auth",
        reportable: false,
        server: false,
    },
    access_denied: {
        title: "You don’t have access to this recording.",
        cause: "This recording belongs to someone else and hasn’t been shared with you at that level.",
        steps: ["Ask the recording’s owner to share it with you."],
        action: "none",
        audio: "unknown",
        area: "auth",
        reportable: false,
        server: false,
    },
    recording_not_found: {
        title: "That recording no longer exists.",
        cause: "It may have been deleted.",
        steps: ["Go back to your recordings list."],
        action: "none",
        audio: "unknown",
        area: "web",
        reportable: false,
        server: false,
    },
    budget_exceeded: {
        title: "Monthly cost cap reached.",
        cause: "Your (or Umbrage’s) monthly transcription budget is used up, so new work is paused.",
        steps: [
            "Ask a Scribe admin to raise the budget in Admin → Budgets.",
            "Try again once it’s raised, or wait for next month’s budget.",
        ],
        action: "contact_admin",
        actionLabel: "Contact an admin about the budget",
        audio: "unknown",
        area: "billing",
        reportable: false,
        server: false,
    },
    invalid_request: {
        title: "Scribe couldn’t accept that request.",
        cause: "The server rejected the request — often a language and vocabulary that don’t go together.",
        steps: [
            "Check the language and vocabulary choices.",
            "Try again.",
            REPORT_STEP,
        ],
        action: "report",
        actionLabel: "Report this problem",
        audio: "unknown",
        area: "web",
        reportable: true,
        server: false,
    },
    transcription_in_flight: {
        title: "A transcription is already running.",
        cause: "This recording is already being transcribed, so a second run wasn’t started.",
        steps: ["Wait for the current run to finish — this page updates on its own."],
        action: "none",
        audio: "safe",
        area: "transcription",
        reportable: false,
        server: false,
    },
    pipeline_unavailable: {
        title: "Transcription couldn’t start.",
        cause: "The transcription service didn’t accept the job. Nothing ran, and your audio is safe.",
        steps: ["Wait a minute, then click “Retry transcription”.", REPORT_STEP],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: false,
        server: false,
    },
    rate_limited: {
        title: "Too many reports.",
        cause: "You’ve sent several reports recently. Thanks — we have them.",
        steps: ["Please wait an hour before sending another."],
        action: "none",
        audio: "unknown",
        area: "web",
        reportable: false,
        server: false,
    },
    transcription_job_failed: {
        title: "Transcription failed.",
        cause: "The transcription engine couldn’t process this recording. Your audio is safe.",
        steps: [
            "Click “Retry transcription”.",
            "If it fails again, try without a custom vocabulary.",
            REPORT_STEP,
        ],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: true,
        server: true,
    },
    audio_unsupported_format: {
        title: "This audio format isn’t supported.",
        cause: "The transcription engine couldn’t read the file’s format.",
        steps: [
            "Convert the file to MP3, M4A, WAV or WebM.",
            "Import the converted file.",
        ],
        action: "none",
        audio: "safe",
        area: "transcription",
        reportable: false,
        server: true,
    },
    audio_too_short_or_silent: {
        title: "There wasn’t enough speech to transcribe.",
        cause: "The recording is too short or almost silent, so there was nothing for the engine to transcribe.",
        steps: [
            "Play the recording to check it has audible speech.",
            "If the microphone was muted, record again with it on.",
        ],
        action: "none",
        audio: "safe",
        area: "transcription",
        reportable: false,
        server: true,
    },
    audio_too_long_or_large: {
        title: "This recording is too long or too large.",
        cause: "It exceeds the transcription engine’s limit (about 4 hours or 2 GB per file).",
        steps: ["Split the audio into shorter parts.", "Import each part separately."],
        action: "none",
        audio: "safe",
        area: "transcription",
        reportable: false,
        server: true,
    },
    vocabulary_rejected: {
        title: "The custom vocabulary was rejected.",
        cause: "The transcription engine refused the vocabulary selected for this recording. Your audio is safe.",
        steps: [
            "Choose “None” (or a different vocabulary) in the options below.",
            "Click “Retry transcription”.",
            "Ask the vocabulary’s editor to check it on the Vocabulary screen.",
        ],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: false,
        server: true,
    },
    transcribe_request_rejected: {
        title: "The transcription settings were rejected.",
        cause: "The engine refused this combination of language and options. Your audio is safe.",
        steps: [
            "Pick a single language (English or Spanish) and no vocabulary.",
            "Click “Retry transcription”.",
            REPORT_STEP,
        ],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: true,
        server: true,
    },
    live_engine_error: {
        title: "Transcription stopped mid-session.",
        // Deliberately makes NO claim about the audio: the bridge also closes 1011 when only the
        // finalize tail failed and the master is safely written (outstanding-work §2.10). Whether
        // audio survived is for the caller to say (RecordScreen's acked-audio note; the detail
        // screen's player).
        cause: "The transcription engine failed while this session was running.",
        steps: [
            "If the recording has audio, click “Retry transcription” to transcribe it.",
            "To keep recording the meeting, start a new session.",
        ],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "partial",
        area: "transcription",
        reportable: true,
        server: true,
    },
    pipeline_never_started: {
        title: "Transcription never started.",
        cause: "The job was queued but didn’t start within 30 minutes. Your audio is safe.",
        steps: ["Click “Retry transcription”.", REPORT_STEP],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: true,
        server: true,
    },
    pipeline_crashed: {
        title: "Transcription stopped unexpectedly.",
        cause: "Something on our side failed while processing this recording. Your audio is safe.",
        steps: ["Click “Retry transcription”.", REPORT_STEP],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: true,
        server: true,
    },
    pipeline_timed_out: {
        title: "Transcription took too long.",
        cause: "The job ran past the time limit and was stopped. Your audio is safe.",
        steps: ["Click “Retry transcription”.", "For very long meetings, split the audio into parts.", REPORT_STEP],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: true,
        server: true,
    },
    pipeline_aborted: {
        title: "Transcription was cancelled.",
        cause: "The job was stopped before it finished (usually by maintenance). Your audio is safe.",
        steps: ["Click “Retry transcription”."],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: false,
        server: true,
    },
    persist_failed: {
        title: "The transcript couldn’t be saved.",
        cause: "Transcription finished but saving the result failed. Your audio is safe.",
        steps: ["Click “Retry transcription”.", REPORT_STEP],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: true,
        server: true,
    },
    live_language_rejected: {
        title: "Live transcription couldn’t use this language setting.",
        // Same audio stance as `live_engine_error`: the bridge stops at the failure, so whatever
        // was captured before it is saved and nothing after.
        cause: "The live engine refused the language setting (Combined or auto-detect) for this session.",
        steps: [
            "If the recording has audio, click “Retry transcription”. The full transcription supports Combined.",
            "For your next live session, pick a single language (English or Spanish).",
            REPORT_STEP,
        ],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "partial",
        area: "transcription",
        reportable: true,
        server: true,
    },
    stuck_in_progress: {
        title: "This recording seems stuck.",
        cause: "It has been “in progress” far longer than any transcription takes. Your audio is safe.",
        steps: ["Click “Retry transcription”.", REPORT_STEP],
        action: "retry_transcription",
        actionLabel: "Retry transcription",
        audio: "safe",
        area: "transcription",
        reportable: true,
        server: false,
    },
    unknown: {
        title: "Something went wrong.",
        cause: "Scribe hit an error it doesn’t have a specific explanation for yet.",
        steps: ["Try again.", REPORT_STEP],
        action: "report",
        actionLabel: "Report this problem",
        audio: "unknown",
        area: "web",
        reportable: true,
        server: false,
    },
};
/** Every catalogue code, in declaration order. */
export const RECORDING_PROBLEM_CODES = Object.keys(RECORDING_PROBLEMS);
export function isRecordingProblemCode(value) {
    return typeof value === "string" && Object.prototype.hasOwnProperty.call(RECORDING_PROBLEMS, value);
}
/** The catalogue entry for `code`; an unrecognized code (e.g. one a newer backend added)
 * degrades to `unknown` rather than throwing, so an old client never renders nothing. */
export function getRecordingProblem(code) {
    const key = isRecordingProblemCode(code) ? code : "unknown";
    return { code: key, ...RECORDING_PROBLEMS[key] };
}
//# sourceMappingURL=catalogue.js.map